import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { PedidoService } from '../../core/services/pedido.service';
import { AlertService } from '../../core/services/alert.service';
import { UiLoadingComponent } from '../../shared/components/ui-loading.component';
import { PedidoListApi } from '../../core/models/api/pedido-api';

@Component({
    selector: 'app-admin-verificados',
    standalone: true,
    imports: [CommonModule, FormsModule, UiLoadingComponent],
    template: `
    <div class="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 class="text-2xl font-black text-gray-800">En preparación</h2>
        <p class="text-sm text-gray-400 mt-1">Pedidos con pago confirmado. Cuando el pedido esté armado, márcalo listo para salir a ruta.</p>
      </div>
      <label class="block min-w-60">
        <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Buscar</span>
        <input [ngModel]="key()" (ngModelChange)="buscar($event)" placeholder="Nro, correo, teléfono, doc…" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200 bg-white" />
      </label>
    </div>

    <app-ui-loading *ngIf="loading()"></app-ui-loading>

    <div *ngIf="!loading()" class="bg-white rounded-[2rem] border border-rose-100 shadow-sm overflow-hidden">
      <table class="w-full text-sm">
        <thead>
          <tr class="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
            <th class="px-5 py-3">Nro</th>
            <th class="px-5 py-3">Estado</th>
            <th class="px-5 py-3">Total</th>
            <th class="px-5 py-3">Entrega</th>
            <th class="px-5 py-3"></th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let p of pedidos()" class="border-b border-gray-50">
            <td class="px-5 py-3">
              <p class="font-black text-gray-800">#{{ p.numeroPedido }}</p>
              <p class="text-[11px] text-gray-400">{{ p.correoContacto }}</p>
            </td>
            <td class="px-5 py-3"><span class="text-[10px] font-bold uppercase px-2 py-1 rounded-full bg-emerald-50 text-emerald-600">{{ p.estado }}</span></td>
            <td class="px-5 py-3 font-bold text-rose-500">S/ {{ p.total.toFixed(2) }}</td>
            <td class="px-5 py-3 text-gray-600">
              <p>{{ p.fechaEntrega }} · {{ p.rangoHorario }}</p>
              <p *ngIf="p.tipoEntrega" class="text-[11px] text-gray-400">{{ p.tipoEntrega }}<span *ngIf="p.direccion"> · {{ p.direccion }}</span></p>
            </td>
            <td class="px-5 py-3 text-right">
              <button type="button" (click)="marcarListo(p)" class="px-4 py-2 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold cursor-pointer whitespace-nowrap">Listo para ruta</button>
            </td>
          </tr>
        </tbody>
      </table>
      <p *ngIf="pedidos().length === 0" class="px-5 py-8 text-center text-sm text-gray-400">Nada pendiente de preparar.</p>
    </div>
    `
})
export class AdminVerificadosComponent implements OnInit {
    private pedidosService = inject(PedidoService);
    private alerts = inject(AlertService);

    loading = signal(true);
    key = signal('');
    pedidos = signal<PedidoListApi[]>([]);
    private searchTimer: ReturnType<typeof setTimeout> | null = null;

    ngOnInit(): void {
        this.cargar();
    }

    buscar(term: string) {
        this.key.set(term);
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
        }
        this.searchTimer = setTimeout(() => this.cargar(), 400);
    }

    marcarListo(p: PedidoListApi) {
        this.pedidosService.cambiarEstado(p.uuid, 'LISTO', null).subscribe({
            next: () => {
                this.alerts.ok(`Pedido #${p.numeroPedido} listo para salir a ruta`);
                this.cargar();
            },
            error: (err) => this.alerts.error(err?.message || 'No se pudo marcar listo.')
        });
    }

    private cargar() {
        this.loading.set(true);
        forkJoin([
            this.pedidosService.listPedidosStaff('PAGO_CONFIRMADO', this.key() || undefined, 1, 50),
            this.pedidosService.listPedidosStaff('EN_PREPARACION', this.key() || undefined, 1, 50)
        ]).subscribe({
            next: ([a, b]) => {
                this.pedidos.set([...a.data, ...b.data].sort((x, y) => y.createdAt.localeCompare(x.createdAt)));
                this.loading.set(false);
            },
            error: (err) => {
                this.loading.set(false);
                this.alerts.error(err?.message || 'No se pudieron cargar los pedidos.');
            }
        });
    }
}
